'use client';

import { usePathname } from 'next/navigation';
import { FC, useEffect } from 'react';
import { debounce } from 'throttle-debounce';

import { useAuth } from '@sola/auth-sdk/store.js';
import { useCart } from '@sola/cart-sdk/store.js';
// import { useAuth } from '@sola/auth-sdk/store.js';
import { generalConfig } from '@sola/config/lib/getters.js';
import { funnelLabels } from '@sola/contentful-model/lib/taxonomy-values.js';
import {
  AuthClientResponse,
  UserLoginState,
} from '@sola/schema/dotcom/auth.js';
import {
  CartItem,
  CartItemTypeEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import { Cart } from '@sola/schema/dotcom/cart/cart.js';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.js';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.js';
import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { FundProps } from '@sola/schema/lib/schema.js';
import { mapAnalyticsCartItems } from '@sola/shared-utils/analytics.js';

const trackEvent = (event: AnyObject) => {
  if (!window) {
    return;
  }
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push(event);
};

// https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag
export const trackPageView = () => {
  trackEvent({
    event: 'page_view',
    page_location: window.location.href,
    page_title: document.title,
    send_page_view: true,
  });
};

export const TrackPageView: FC = () => {
  const pathname = usePathname();
  useEffect(() => {
    trackPageView();
  }, [pathname]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287630/Clicks
// @TODO: Add click tracking to lots of places.
export const trackClick = (
  label: string,
  link?: string,
  classname?: string,
  elementId?: string,
) => {
  trackEvent({
    event: 'element_click',
    // The inner text of the element clicked
    element_label: label,
    // The `href` attribute of the element clicked - sometimes null
    element_link: link || null,
    // The CSS class name of the element clicked - sometimes null
    element_class_name: classname || null,
    // The `id` HTML attribute of the element clicked - sometimes null
    element_id: elementId || null,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29025861652/Generate+Lead
export const trackLeadFormSuccess = (formIdentifier: string) => {
  trackEvent({
    event: 'generate_lead',
    // The name of the lead generation form
    lead_source: formIdentifier,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582549/Search
export const trackSearch = (searchTerm?: string) => {
  // Don't track empty strings
  if (!searchTerm) {
    return;
  }
  trackEvent({
    event: 'search',
    // The term the user searched for
    search_term: searchTerm,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582663/Funnel+Page+View
export const trackFunnelPage = (funnel?: string | null) => {
  if (!funnel || !funnelLabels[funnel as keyof typeof funnelLabels]) {
    return;
  }
  trackEvent({
    event: 'funnel_page_view',
    funnel_category: funnelLabels[funnel as keyof typeof funnelLabels],
  });
};

export const TrackFunnelPage: FC<{ funnel?: string | null }> = ({ funnel }) => {
  useEffect(() => {
    trackFunnelPage(funnel);
  }, [funnel]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027893249/Select+Content
export const trackSelectContent = (
  cmsId: string,
  title: string,
  type: string,
  tags: string[],
) => {
  trackEvent({
    event: 'select_content',
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026091384/View+Content
export const trackViewContent = (
  cmsId: string,
  title: string,
  type: string,
  tags: string[],
) => {
  trackEvent({
    event: 'view_content',
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags,
  });
};

export const TrackViewContent: FC<{
  cmsId: string;
  title?: string;
  type?: string;
  tags?: string[];
}> = ({ cmsId, title, type, tags }) => {
  useEffect(() => {
    trackViewContent(cmsId, title || 'Unknown', type || 'Unknown', tags || []);
  }, [cmsId, title, type, tags]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026385954/Search+for+a+Child
export type ChildSearchEvent = {
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  region?: string;
  country?: string | string[];
  birthMonth?: number;
  birthDay?: number;
  orphan?: boolean;
  disability?: boolean;
  highlyVulnerable?: boolean;
  hiv?: boolean;
};
export const trackChildSearch = (event: ChildSearchEvent) => {
  trackEvent({
    event: 'search_for_a_child',
    filter_gender: event.gender,
    filter_age_min: event.ageMin,
    filter_age_max: event.ageMax,
    filter_region: event.region,
    filter_country: Array.isArray(event.country)
      ? event.country.join(', ')
      : event.country,
    filter_birth_month: event.birthMonth,
    filter_birth_day: event.birthDay,
    filter_orphan: event.orphan,
    filter_disabilities: event.disability,
    filter_hvc: event.highlyVulnerable,
    filter_hiv: event.hiv,
  });
};

export const TrackChildSearch: FC<{ event: ChildSearchEvent }> = ({
  event,
}) => {
  useEffect(() => {
    debounce(5000, () => {
      trackChildSearch(event);
    })();
  }, [JSON.stringify(event)]);
  return null;
};

const getFundCodeFrequency = (fund: FundProps) => {
  const defaultResult = {
    commitmentFrequency: CommitmentFrequencyEnum.OneTime,
    fundCode: fund.fundCodeOneTime,
  };
  if (fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return defaultResult;
  }
  if (fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return {
      commitmentFrequency: CommitmentFrequencyEnum.Monthly,
      fundCode: fund.fundCodeCommitment || fund.fundCodeOneTime,
    };
  }

  return defaultResult;
};

const convertFundToCart = (
  fund: FundProps,
  isCatalog?: boolean,
): Partial<CartItem> => {
  const { commitmentFrequency, fundCode } = getFundCodeFrequency(fund);
  return {
    itemType: CartItemTypeEnum.Fund,
    itemCode: fundCode,
    title: fund.fundTitle,
    // TODO: Ideally we'd be able to pull the default selection from a component
    commitmentFrequency: isCatalog
      ? CommitmentFrequencyEnum.OneTime
      : commitmentFrequency,
    unitPrice: isCatalog
      ? (fund.catalogAmount ?? 0)
      : fund.suggestedAmount ||
        fund.suggestedAmountsOneTime?.find((a) => a.isDefault)?.value ||
        fund.suggestedAmountsMonthly?.find((a) => a.isDefault)?.value ||
        fund.suggestedAmountsAnnually?.find((a) => a.isDefault)?.value ||
        0,
    quantity: 1,
    fund: {
      fundCode: fundCode || '',
    },
  };
};

export const TrackViewFund: FC<{
  fund?: FundProps;
}> = ({ fund }) => {
  useEffect(() => {
    if (!fund) {
      return;
    }

    trackEvent({
      event: 'view_item',
      items: mapAnalyticsCartItems([convertFundToCart(fund)]),
      currency: 'USD',
    });
  }, [fund?.id]);
  return null;
};

export const trackViewFundItemList = (
  listId: string,
  listName: string,
  funds: FundProps[],
  isCatalog: boolean,
) => {
  if (!funds?.length) {
    return;
  }

  trackEvent({
    event: 'view_item_list',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(
      funds.map((fund) => convertFundToCart(fund, isCatalog)),
    ),
    currency: 'USD',
  });
};

export const TrackViewFundItemList: FC<{
  listId: string;
  listName: string;
  items: FundProps[];
  isCatalog?: boolean;
}> = ({ listId, listName, items, isCatalog = false }) => {
  useEffect(() => {
    if (!items?.length) {
      return;
    }
    trackViewFundItemList(listId, listName, items, isCatalog);
  }, [listId, listName, JSON.stringify(items)]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027696645/Select+Item
export const trackSelectFundItem = (
  listId: string,
  listName: string,
  fund?: FundProps,
  isCatalog = false,
) => {
  if (!fund) {
    return;
  }
  trackEvent({
    event: 'select_item',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems([convertFundToCart(fund, isCatalog)]),
    currency: 'USD',
  });
};

const convertChildToCart = (item: ChildAvailabilityItem): Partial<CartItem> => {
  return {
    // TODO: Ideally this would come from the SDG ContentSettings.fundCodeCdsp
    itemCode: 'DS',
    itemType: CartItemTypeEnum.Sponsorship,
    commitmentFrequency: CommitmentFrequencyEnum.Monthly,
    quantity: 1,
    sponsorship: {
      sponsorshipType: SponsorshipItemTypeEnum.Selected,
      globalId: item.globalId,
      sourceCode: item.sourceCode ?? undefined,
    },
    child: {
      name: item.name?.preferred,
      globalId: item.globalId,
      localId: item.localId,
      imageUrl: '',
      birthDate: '',
      country: item.fieldOffice,
    },
    // TODO: Currently a non-exported constant in cart's sponsorship.ts
    title: 'Child Sponsorship',
    // TODO: Ideally this would come from the SDG ContentSettings.defaultCdspRate or a subsidy...
    unitPrice: generalConfig.rate,
  };
};

export const trackViewSacItemList = (
  listId: string,
  listName: string,
  items: ChildAvailabilityItem[],
) => {
  if (!items?.length) {
    return;
  }
  trackEvent({
    event: 'view_item_list',
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(items.map(convertChildToCart)),
    currency: 'USD',
  });
};

export const TrackViewSacItemList: FC<{
  listId: string;
  listName: string;
  items: ChildAvailabilityItem[];
}> = ({ listId, listName, items }) => {
  useEffect(() => {
    if (!items?.length) {
      return;
    }
    trackViewSacItemList(listId, listName, items);
  }, [listId, listName, JSON.stringify(items)]);
  return null;
};

export const TrackViewBio: FC<{
  child: ChildAvailabilityItem;
}> = ({ child }) => {
  // @TODO: Handle subsidized rate
  useEffect(() => {
    if (!child) {
      return;
    }
    trackEvent({
      event: 'view_item',
      items: mapAnalyticsCartItems([convertChildToCart(child)]),
      currency: 'USD',
    });
  }, [child.globalId]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287637/Errors
// @TODO: Add error tracking to lots of places.
export const trackError = (errorMessage?: string) => {
  if (!errorMessage) {
    return;
  }
  trackEvent({
    event: 'error_message',
    // The error message presented to the user
    message: errorMessage,
  });
};

export const TrackError: FC<{ errorMessage?: string }> = ({ errorMessage }) => {
  useEffect(() => {
    trackError(errorMessage);
  }, [errorMessage]);
  return null;
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29089169443/User+Identification
export const trackUser = (
  authUser?: AuthClientResponse | null,
  cart?: Cart | null,
) => {
  // TODO: Probably should consolidate with sendInitialPushEvent from shared-utils
  const loggedIn = authUser && !!authUser.authUserGlobalId;
  trackEvent({
    event: 'initial_push',
    is_logged_in: loggedIn,
    logged_in_state: loggedIn ? UserLoginState.LoggedIn : UserLoginState.Guest,
    // TODO: determine source code if no cart?
    source_code_id: cart?.sourceCode,
    supporter_global_id: authUser?.authUserGlobalId,
    user_id: authUser?.authUserPrimaryContactId,
  });
};

export const TrackUser: FC = () => {
  const pathname = usePathname();
  const { isReady: isAuthReady, authUser } = useAuth(
    'isReady',
    'isLoggedIn',
    'authUser',
  );
  const { isReady: isCartReady, cart } = useCart('isReady', 'cart');

  useEffect(() => {
    if (!isAuthReady || !isCartReady) {
      return;
    }
    trackUser(authUser, cart);
  }, [pathname, isAuthReady, isCartReady]);

  return null;
};
